/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// localStorage cache for Google Places API responses.
// Every entry carries a `ts` timestamp; anything older than the supplied TTL
// (default 24h) is treated as a miss and refetched. Keys are namespaced so
// they don't collide with other app state. Callers can pass a shorter ttlMs
// when they want fresher data (e.g. map nearby places use 1 hour).

const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

interface CacheEntry<T> {
  ts: number;
  data: T;
}

function read<T>(key: string, ttlMs: number): T | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry<T>;
    if (typeof parsed?.ts !== "number" || Date.now() - parsed.ts > ttlMs) {
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function write<T>(key: string, data: T): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // Quota, private browsing, etc. — cache is best-effort.
  }
}

// Round coordinates to 3 decimals (~110m bucket) so GPS drift doesn't miss cache.
function roundCoord(n: number): string {
  return (Math.round(n * 1000) / 1000).toFixed(3);
}

function nearbyKey(lat: number, lng: number, type: string): string {
  return `places_nearby_${roundCoord(lat)}_${roundCoord(lng)}_${type}`;
}

function detailsKey(placeId: string): string {
  return `places_cache_${placeId}`;
}

export function getCachedNearby<T = unknown>(
  lat: number,
  lng: number,
  type: string,
  ttlMs: number = DEFAULT_TTL_MS,
): T | null {
  return read<T>(nearbyKey(lat, lng, type), ttlMs);
}

export function setCachedNearby<T = unknown>(
  lat: number,
  lng: number,
  type: string,
  data: T,
): void {
  write(nearbyKey(lat, lng, type), data);
}

export function getCachedPlaceDetails<T = unknown>(
  placeId: string,
  ttlMs: number = DEFAULT_TTL_MS,
): T | null {
  if (!placeId) return null;
  return read<T>(detailsKey(placeId), ttlMs);
}

export function setCachedPlaceDetails<T = unknown>(
  placeId: string,
  data: T,
): void {
  if (!placeId) return;
  write(detailsKey(placeId), data);
}
